/**
 * createStackFromBlocks
 *
 * Shared "create a Stack and move N blocks into it" flow, used by:
 *   - the multiselect action bar's "Create stack" action
 *   - the drag-to-create-stack gesture in `<Stream>` (drop a block on
 *     another block — works against Group, Primary Group, or
 *     Collection parents under `LEGAL_PARENT_CHILD_LAYER_PAIRS`)
 *
 * Latency model — every visible piece of UI lands at T+0:
 *
 *   1. Pre-allocate the Stack id on the client (`generateId("collection")`).
 *      The id is byte-identical to what the server will store, so
 *      every optimistic write below is keyed against the canonical id.
 *   2. Synchronously kick off `createStack` with that id as
 *      `clientId`. The store's helper writes the placeholder Stack
 *      to the cross-layer cache + parent-children query in the same
 *      tick; the server `apiCreateStack` POST is awaited internally.
 *      We DON'T await the returned promise here — we capture it.
 *   3. Synchronously kick off `moveBlocksBetweenParentChild` against
 *      the pre-allocated id, passing `awaitBefore: createPromise` so
 *      the helper's optimistic block-envelope `collectionId` patch
 *      fires NOW (the homepage Stream's synthetic-stack grouping
 *      picks it up in the same React commit) but the server
 *      `move-blocks` POST holds until the companion `createStack`
 *      has confirmed the target id server-side (otherwise the move
 *      would 404).
 *   4. Suggestion (Haiku) fires in parallel; the rename writes
 *      against `placeholderId` once available.
 *
 * Net effect: drop-on-block produces an inline Stack tile in the
 * same frame, no waiting on the server.
 *
 * Failure handling:
 *   - `createStack` throws → its own catch rolls back the
 *     placeholder. The move helper's `awaitBefore` rejects, which
 *     trips the move's own catch and rolls back the optimistic
 *     block move (envelope `collectionId` restored). Both errors
 *     bubble out; we rethrow the create error verbatim.
 *   - Move throws after create succeeds → we `softDelete` the
 *     newly-created Stack so the placeholder doesn't linger as a
 *     half-applied row.
 *
 * Callers handle UI side effects (toast, exit multiselect, etc.) —
 * the helper is data-layer only.
 */
import { suggestStackName } from "@/api/query-api";
import { collectionStore } from "@/data/collection-store";
import { ConfiguredLogger } from "@/lib/logger";
import { generateId } from "@shared/types/id";
const logger = new ConfiguredLogger({ caller: "createStackFromBlocks" });
// Initial title before the Haiku suggestion lands. Matches the
// fallback in `stack-block-view.tsx` (`stack.title || "Untitled stack"`)
// so the optimistic tile reads exactly the same as a Stack whose
// title happens to be missing — no UX special-case for the in-flight
// window.
const PLACEHOLDER_STACK_TITLE = "Untitled stack";
/**
 * Optimistically create a Stack under `parentCollectionId` and move
 * `blockIds` into it. Returns the new Stack id, move count, and any
 * skipped block ids. Throws on server failure for create or move; the
 * caller decides how to surface the error.
 */
export async function createStackFromBlocks(parentCollectionId, blockIds) {
    // Pre-allocate the Stack id on the client so every optimistic write
    // below keys against the canonical (eventual server) id. `createStack`
    // forwards this as `clientId` to the server, which echoes the same
    // id back in its response.
    const placeholderId = generateId("collection");
    // Fire the Haiku-backed name suggestion in parallel with the
    // create. The placeholder title is what shows on the new tile until
    // the suggestion lands; if the LLM call fails (network blip, server
    // overload) the placeholder just sticks. Critical path doesn't
    // block on the ~500–1500ms call.
    const suggestionPromise = suggestStackName(parentCollectionId, blockIds).catch((e) => {
        logger.warn("Failed to suggest stack name", {
            parentCollectionId,
            error: e instanceof Error ? e.message : String(e),
        });
        return undefined;
    });
    // 1. Stack placeholder lands in caches synchronously inside
    //    `createStack`; the returned promise resolves when the server
    //    has confirmed. We capture it (not await) so the move below can
    //    run its optimistic writes IMMEDIATELY without waiting for the
    //    network round trip.
    const createPromise = collectionStore.createStack(parentCollectionId, {
        title: PLACEHOLDER_STACK_TITLE,
        clientId: placeholderId,
    });
    // 2. Block move's optimistic writes fire now — block envelopes
    //    flip their `collectionId` to `placeholderId`, scope-index rows
    //    move, count pills bump. The Stream re-renders in this tick
    //    and the synthetic-stack grouping clusters the moved blocks
    //    under the new Stack tile inline. The server `move-blocks` POST
    //    waits for `createPromise` to settle (otherwise it would 404
    //    against an id the server hasn't created yet).
    const movePromise = collectionStore.moveBlocksBetweenParentChild(blockIds, parentCollectionId, placeholderId, { awaitBefore: createPromise });
    try {
        // Await BOTH server calls before returning. We sequence the
        // awaits so a `createStack` failure surfaces verbatim (and the
        // move's own catch has already restored its optimistic writes
        // via the `awaitBefore` rejection path).
        await createPromise;
        const moveResult = await movePromise;
        // Background rename. `updateTitle` is optimistic: it patches the
        // cross-layer cache + every parent-children query cache in the
        // same React commit, so the user sees the suggested title flip
        // in without a refetch. The `=== PLACEHOLDER` no-op covers the
        // edge case where the LLM (or its server-side numeric fallback)
        // happens to return our placeholder string.
        void suggestionPromise.then(async (suggestedTitle) => {
            if (!suggestedTitle)
                return;
            if (suggestedTitle === PLACEHOLDER_STACK_TITLE)
                return;
            try {
                await collectionStore.updateTitle(placeholderId, suggestedTitle);
            }
            catch (e) {
                logger.warn("Failed to apply suggested stack title", {
                    stackId: placeholderId,
                    error: e instanceof Error ? e.message : String(e),
                });
            }
        });
        return {
            stackId: placeholderId,
            movedCount: moveResult.movedCount,
            skipped: moveResult.skipped,
        };
    }
    catch (e) {
        // We need to know whether the create succeeded to decide whether
        // to clean up. If `createPromise` resolved (e.g. move failed
        // post-create), we've left a freshly-created Stack with no
        // contents server-side — soft-delete it. If create rejected,
        // its own catch already rolled back the placeholder cache rows;
        // the move's `awaitBefore` would have rejected too, restoring
        // the envelope `collectionId`s. Nothing else to do here.
        let createSucceeded = false;
        try {
            await createPromise;
            createSucceeded = true;
        }
        catch {
            // create itself failed — no cleanup needed.
        }
        if (createSucceeded) {
            void collectionStore.softDelete(placeholderId).catch((cleanupError) => {
                logger.warn("Failed to roll back stack after create flow failed", {
                    stackId: placeholderId,
                    error: cleanupError instanceof Error ? cleanupError.message : String(cleanupError),
                });
            });
        }
        throw e;
    }
}
